<template>
    <div>
        <div class="content-frame" :class="{'container is-max-widescreen': enrollment.course.content_framed}">

            <lesson-header :component="component" :enrollment="enrollment" :outline="outline"/>

            <render-blocks :blocks="content.content.question" :enrollment="enrollment" :outline="outline"/>

            <hr class="mt-5 mb-6"/>

            <div v-if="content.attributes.type === 'ReflectionCheckpoint'">
                <div class="question-container ml-4 mr-6">
                    <div class="question-lines">
                        <div class="question-gutter">
                            <div v-if="enrollment.data[id].submitted" class="question-area">
                                <div class="question">{{answer}}</div>
                            </div>
                            <div v-else>
                                <div class="question-area">
                                    <contenteditable tag="div" v-model="answer" class="question"/>
                                </div>
                                <div v-if="!answer" class="question-placeholder">Type your answer here...</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-else-if="content.attributes.type === 'MultiChoiceCheckpoint'">
                <div class="checkmarks-container">
                    <div v-for="a in content.content.answers">
                        <b-checkbox type="is-dark" v-model="enrollment.data[a.id]" size="is-medium" class="mb-5 ml-2 pt-1" @input="saveData(a.id, a)" :disabled="enrollment.data[id].submitted">{{ a.answer }}</b-checkbox>
                    </div>
                </div>
                <hr class="mt-5 mb-6"/>
            </div>

            <div v-else-if="content.attributes.type === 'SingleChoiceCheckpoint'">
                <div class="checkmarks-container">
                    <div v-for="a in content.content.answers">
                        <b-radio type="is-dark" v-model="enrollment.data[id].answer" name="checkpoint" size="is-medium" class="mb-5 ml-2 pt-1" @input="saveData(id, a)" :native-value="a.id" :disabled="enrollment.data[id].submitted">{{ a.answer }}</b-radio>
                    </div>
                </div>
                <hr class="mt-5 mb-6"/>

            </div>
            <div v-else-if="content.attributes.type === 'TextMatchCheckpoint'">
                <div class="columns matching-container" v-for="a in content.content.answers">
                    <div class="column">
                        <div class="key" :class="{'answered': enrollment.data[a.id]}">{{ a.key }}</div>
                    </div>
                    <div class="column is-narrow">
                        <i class="fas fa-arrow-right mt-4 pt-1"></i>

                    </div>
                    <div class="column">
                        <div class="answer-background">
                            <div
                                v-if="enrollment.data[a.id]"
                                class="answer"
                                :class="{'active': a.show, 'graded': enrollment.data[id].submitted, 'correct': a.id === enrollment.data[a.id].id, 'incorrect': a.id !== enrollment.data[a.id].id}"
                                @click="toggleChooser(a)"
                            >
                                {{ enrollment.data[a.id].answer }}
                            </div>
                            <div v-else class="answer choose-button has-text-centered has-text-weight-bold" :class="{'active': a.show}" @click="toggleChooser(a)">
                                <i class="fas fa-plus mr-2"></i>Choose Match
                            </div>

                            <div class="match-chooser" v-if="a.show">

                                <div
                                    v-for="(x, i) in options"
                                    class="key-option"
                                    @click="chooseMatch(x, a)"
                                    :class="{'used': x.used}"
                                    v-if="!enrollment.data[a.id] || x.id !== enrollment.data[a.id].id"
                                >
                                    <b-tooltip v-if="x.used" :delay="500" label="Choosing will remove a previous match." position="is-left" type="is-dark">{{ x.answer }}</b-tooltip>
                                    <div v-else>{{ x.answer }}</div>
                                </div>

                            </div>
                        </div>


                    </div>
                </div>
                <hr class="mt-5 mb-6"/>

            </div>


            <div class="columns">
                <div class="column is-narrow">
                    <b-button v-if="!enrollment.data[this.id].submitted" type="is-dark" @click="submit" class="ml-5 mb-4"><i class="fas fa-check mr-3"></i>Submit</b-button>
                    <b-button v-else type="is-dark" @click="retry" class="ml-5 mb-4"><i class="fas fa-refresh mr-3"></i>Retry Checkpoint</b-button>
                </div>
                <div class="column" v-if="content.attributes.survey">
                    * Your answer(s) will not have any impact on your progress
                </div>
            </div>

            <div v-if="enrollment.data[id].submitted" class="results-container" :class="{'correct': enrollment.data[id].correct, 'incorrect': !enrollment.data[id].correct}">
                <div class="columns is-gapless">
                    <div class="column is-narrow"><i class="far fa-2x fa-fw" :class="{'fa-circle-check': enrollment.data[id].correct, 'fa-times-circle': !enrollment.data[id].correct}"></i></div>
                    <div class="column">
                        <render-blocks :blocks="(enrollment.data[id].correct) ? content.content.correct : content.content.incorrect"/>
                    </div>
                </div>
            </div>

<!--            <json-viewer :value="options"></json-viewer>-->
<!--            <json-viewer :value="content"></json-viewer>-->
<!--            <json-viewer :value="enrollment.data"></json-viewer>-->

        </div>

        <div class="content-controls">
            <div class="columns">
                <div class="column" v-if="!enrollment.completed_at">
                    <div>
                        <b-tooltip position="is-right" type="is-white" class=" mr-4">
                            <template v-slot:content>
                                <i class="fas fa-check mr-1"></i>Data automatically saved to the cloud.
                            </template>
                            <i class="fad fa-cloud-check fa-lg"></i>
                        </b-tooltip>
                        <template v-if="enrollment.course.show_timer">
                            <i class="fas fa-circle fa-xs mr-2" style="color: red;"></i>
                            <strong>Recording Time:</strong> {{ formattedTime }}<span v-if="outline.require_min_time"> of {{ formattedMinTime }} required.</span>
                        </template>
                    </div>
                </div>
                <div class="column has-text-right">
                    <b-button type="is-white" outlined v-if="outline.prevOutlineId" @click="$emit('goto', outline.prevOutlineId)"><i class="fas fa-arrow-left mr-3"></i>Previous</b-button>
                    <b-button type="is-white" outlined class="ml-5" v-if="outline.completed_at && outline.nextOutlineId" @click="$emit('goto', outline.nextOutlineId)">Next<span v-if="outline.counts.ofSection === outline.counts.totalSection"> Section</span><i class="fas fa-arrow-right ml-3"></i></b-button>
                    <b-tooltip v-if="!outline.completed_at" :active="milestoneLock.locked" position="is-top" type="is-warning" multilined class="ml-5">
                        <template v-slot:content>
                            <i class="far fa-circle-pause mr-1"></i>Paused &mdash; waiting on your reflection to be approved.
                        </template>
                        <b-button type="is-white" outlined @click="complete" :disabled="milestoneLock.locked">Complete<span v-if="outline.counts.ofSection === outline.counts.totalSection"> Section</span><i class="fas fa-check ml-3"></i></b-button>
                    </b-tooltip>
                </div>
            </div>
        </div>

    </div>
</template>

<script>
    import RenderBlocks from "./RenderBlocks";
    import moment from "moment";
    import {debounce} from 'throttle-debounce';
    import LessonHeader from "./LessonHeader";

    export default {
        components: {LessonHeader, RenderBlocks},
        props: {
            enrollment: {},
            component: {},
            outline: {},
            time: {},
            milestoneLock: {type: Object, default: () => ({locked: false})}
        },
        data: function() {
            return {
                id: null,
                answer: '',
                timer: null,
                options: []
            };
        },
        computed: {
            formattedTime: function() {
                return moment.utc(this.time * 1000).format('HH:mm:ss');
            },
            formattedMinTime: function() {
                return moment.utc(this.outline.require_min_time * 60 * 1000).format('HH:mm:ss');
            },
            formattedLessonTime: function() {
                return moment.utc((this.time * 1000) + (this.outline.time * 60 * 1000)).format('HH:mm:ss');
            },

        },
        methods: {
            toggleChooser(a) {
                if (!this.enrollment.data[this.id].submitted) {
                    a.show = !a.show;
                }
            },
            chooseMatch(match, answer) {
                match.used = true;
                answer.show = false;

                this.enrollment.data[answer.id] = match;

                for (let x = 0; x < this.content.content.answers.length; x++) {
                    const a = this.content.content.answers[x];
                    if (a.id !== answer.id && this.enrollment.data[a.id] && this.enrollment.data[a.id].id === match.id) {
                        this.enrollment.data[a.id] = null;
                    }
                }

                this.strikeout();
                this.saveData(answer.id, match);
            },
            strikeout() {
                for (let i = 0; i < this.options.length; i++) {
                    const option = this.options[i];
                    option.used = false;
                }

                for (let i = 0; i < this.content.content.answers.length; i++) {
                    const a = this.content.content.answers[i];

                    for (let x = 0; x < this.options.length; x++) {
                        const option = this.options[x];
                        if (!option.used && this.enrollment.data[a.id] && option.id === this.enrollment.data[a.id].id) {
                            option.used = true;
                        }
                    }
                }
            },
            saveData(id, block) {
                id = id || this.id;
                let payload = {
                    block: JSON.stringify(block) || JSON.stringify(this.content.content),
                    field: id,
                    value: JSON.stringify(this.enrollment.data[id]),
                    component_id: this.component.id,
                };

                if (this.content.attributes.type === 'ReflectionCheckpoint') {

                } else if (this.content.attributes.type === 'MultiChoiceCheckpoint') {

                } else if (this.content.attributes.type === 'SingleChoiceCheckpoint') {

                } else if (this.content.attributes.type === 'TextMatchCheckpoint') {

                }

                this.$emit('saveData', payload);
                this.timer = null;
            },
            submit() {
                if (this.content.attributes.type === 'ReflectionCheckpoint') {
                    this.enrollment.data[this.id].correct = !!this.answer;
                    this.enrollment.data[this.id].submitted = true;

                } else if (this.content.attributes.type === 'MultiChoiceCheckpoint') {

                    let complete = false;
                    let correct = true;

                    for (let i = 0; i < this.content.content.answers.length; i++) {
                        const a = this.content.content.answers[i];

                        if (this.enrollment.data[a.id]) complete = true;
                        if ((a.correct && !this.enrollment.data[a.id]) || (!a.correct && this.enrollment.data[a.id])) {
                            correct = false;
                            break;
                        }
                    }

                    if (this.content.attributes.survey) {
                        // console.log('In survey mode, considering it correct.');
                        correct = true;
                    }

                    if (!complete) {
                        window.alert('Please select at least one response before submitting.');
                    } else {
                        this.enrollment.data[this.id].correct = correct;
                        this.enrollment.data[this.id].submitted = true;
                    }

                } else if (this.content.attributes.type === 'SingleChoiceCheckpoint') {
                    let complete = !!this.enrollment.data[this.id].answer;
                    let correct = true;

                    for (let i = 0; i < this.content.content.answers.length; i++) {
                        const a = this.content.content.answers[i];

                        if ((a.correct && !this.enrollment.data[a.id]) || (!a.correct && this.enrollment.data[a.id])) {
                            correct = false;
                            break;
                        }
                    }

                    if (this.content.attributes.survey) {
                        // console.log('In survey mode, considering it correct.');
                        correct = true;
                    }

                    if (!complete) {
                        window.alert('Please select a response before submitting.');
                    } else {
                        this.enrollment.data[this.id].correct = correct;
                        this.enrollment.data[this.id].submitted = true;
                    }

                } else if (this.content.attributes.type === 'TextMatchCheckpoint') {
                    let correct = true;
                    let complete = true;

                    for (let i = 0; i < this.content.content.answers.length; i++) {
                        const a = this.content.content.answers[i];
                        if (this.enrollment.data[a.id] && this.enrollment.data[a.id].id) {
                            if (a.id !== this.enrollment.data[a.id].id) {
                                correct = false;
                            }
                        } else {
                            complete = false;
                        }
                    }

                    if (!complete) {
                        window.alert('Please answer all questions before submitting.');
                    } else {
                        this.enrollment.data[this.id].correct = correct;
                        this.enrollment.data[this.id].submitted = true;
                    }
                }

                this.saveData();

            },
            retry() {
                this.enrollment.data[this.id].correct = false;
                this.enrollment.data[this.id].submitted = false;

                if (this.content.attributes.type === 'TextMatchCheckpoint') {
                    for (let i = 0; i < this.content.content.answers.length; i++) {
                        const answer = this.content.content.answers[i];
                        if (answer.id !== this.enrollment.data[answer.id].id) {
                            this.enrollment.data[answer.id] = null;
                        }
                    }
                }

                this.strikeout();
                this.saveData();
            },
            complete() {
                if (this.enrollment.data[this.id].submitted) {

                    if (this.content.attributes.required && !this.enrollment.data[this.id].correct) {
                        window.alert('You must correctly complete this checkpoint before continuing.');
                    } else {
                        this.saveData();
                        this.$emit('complete');
                    }

                } else {
                    window.alert('You must submit a response before continuing.');
                }
            }
        },
        watch: {
            'answer': function() {
                this.enrollment.data[this.id].answer = this.answer;

                if (!this.timer) {
                    this.timer = setTimeout(this.saveData, 2000);
                }
            }
        },
        created() {
            this.content = (typeof this.component.content === "string") ? JSON.parse(this.component.content) : this.component.content;
            if (!this.enrollment.data[this.content.attributes.id]) this.enrollment.data[this.content.attributes.id] = {answer: '', submitted: false, correct: false};  // `answer` is only required for reflection
            this.id = this.content.attributes.id;
            this.answer = this.enrollment.data[this.id].answer;

            if (this.content.attributes.type === 'TextMatchCheckpoint') {
                this.content.content.answers.map(obj => ({...obj, used: false}));

                for (let i = 0; i < this.content.content.answers.length; i++) {
                    const answer = this.content.content.answers[i];
                    answer.show = false;
                    // answer.used = false;
                }

                this.options = [...this.content.content.answers];

                // Shuffle array
                for (let i = this.options.length - 1; i > 0; i--) {
                    this.options[i].used = false;
                    const j = Math.floor(Math.random() * (i + 1));
                    [this.options[i], this.options[j]] = [this.options[j], this.options[i]];
                }

                this.strikeout();
            }
        }
    };

</script>

<style lang="scss" scoped>
    .content-frame {
        position: absolute;
        top: 105px;
        left: 5rem;
        right: 4rem;
        bottom: 7rem;
        background-color: #fff;
        border-radius: 8px;
        box-shadow: 5px 5px 30px 0 rgba(0, 0, 0, 0.3);
        padding: 2rem 2rem 7rem;

        overflow-x: hidden;
        overflow-y: scroll;
    }

    .content-controls {
        position: absolute;
        left: 5rem;
        right: 4rem;
        bottom: 1.5rem;
        padding: 3rem 0 1rem;
        font-size: 0.85rem;

        color: white;

        strong {
            color: white;
        }
    }

    .question-container {
        background-color: #f6f6f6;
        border-radius: 0.5rem;
        margin-bottom: 20px;

        .question-lines {
            padding-left: 40px;
            border-radius: 0.5rem;


            background-image: linear-gradient(#e7e7e7 1px, transparent 1px);
            background-size: 100% 35px;
            background-position: 0 -2px;

            .question-gutter {
                border-left: 1px solid #ccc;
                padding-top: 10px;
                position: relative;
            }

            .question-placeholder {
                color: #888;
                position: absolute;
                top: 42px;
                left: 20px;
            }

            .question-area {
                padding: 20px;
                position: relative;
                z-index: 1000;

                .question {
                    background: transparent;
                    border: 0;
                    outline: none !important;
                    line-height: 35px;
                    padding-top: 8px;
                    padding-bottom: 40px;


                    &:focus {
                        outline-offset: 0px !important;
                        outline: none !important;
                    }
                }
            }
        }
    }

    .checkmarks-container {
        margin-left: 35px;
        margin-bottom: 40px;
    }

    .matching-container {
        margin-left: 35px;
        margin-right: 20px;

        .key {
            background-color: #f4f4f4;
            border-radius: 0.5rem;
            padding: 16px;
        }

        .answer-background {
            background-color: #f4f4f4;
            border-radius: 0.5rem;

            .answer {
                border: 4px solid #f4f4f4;
                padding: 12px;
                cursor: pointer;
                border-radius: 0.5rem;

                &.choose-button {
                    border: 4px solid #363636;

                    background-color: #363636;
                    color: white;
                }

                &.active {
                    background-color: #363636;
                    color: white;
                    border-bottom-left-radius: 0;
                    border-bottom-right-radius: 0;
                }

                &.graded {
                    cursor: default;

                    &.correct {
                        border: 4px solid #378537;
                    }

                    &.incorrect {
                        border: 4px solid #bd3a3a;
                    }
                }
            }

            .key-option {
                padding: 15px;
                border-top: 1px solid #e7e7e7;
                cursor: pointer;

                &.used {
                    text-decoration: line-through;
                }

                &:hover {
                    background-color: #919191;
                    color: white;
                }

                &:last-child {
                    border-bottom-left-radius: 0.5rem;
                    border-bottom-right-radius: 0.5rem;

                }
            }
        }
    }

    .results-container {
        margin-left: 24px;
        margin-right: 46px;
        margin-top: 20px;
        margin-bottom: 100px;
        border: 1px solid #e7e7e7;
        border-radius: 0.5rem;
        padding: 20px 20px 0 30px;

        &.correct {
            border-color: #378537;

            i {
                color: #378537;
            }
        }

        &.incorrect {
            border-color: #bd3a3a;

            i {
                color: #bd3a3a;
            }
        }
    }
</style>
